import React from 'react';

import { EvTableColumn } from '@evinced-private/ui-common';

import { NAME_NOT_PROVIDED } from 'src/apps/wfa/helpers/WfaDashboardConsts';
import { IWfaSessionRow } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { EvLinkLocal } from 'src/common/components/ev-link-local/EvLinkLocal';
import { renderNumberCellFormatter } from 'src/common/components/ev-table/cell-formatters/numberCellFormatter';
import { LabelsChips } from 'src/common/components/labels-chips';
import { LABELS_CHIPS_DAID } from 'src/common/components/labels-chips/LabelsChips.constants';
import { ACTIONS_COLUMN_STYLE, RowActionsMenu } from 'src/common/components/row-actions-menu';
import {
	ENVIRONMENT_LABEL_PREFIX,
	UNIT_ID_LABEL_PREFIX
} from 'src/common/consts/platformConfigConsts';
import { returnNonNull } from 'src/common/helpers/ArraysHelper';
import { formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';
import {
	extractLabelValue,
	isLabelString as checkIsLabelString,
	transformLabelsForChips
} from 'src/common/helpers/LabelDisplayHelpers';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';
import { SessionViews } from 'src/common/types/WfaSessionViews';

export const getWfaSessionsColumns = (
	isNewPlatformEnabled?: boolean,
	unitIdDisplayName?: string,
	unitIdLabelConfig?: ConfigurationLabel,
	environmentDisplayName?: string,
	environmentLabelConfig?: ConfigurationLabel,
	onDeleteSession?: (sessionId: string) => Promise<void>,
	hasMultipleSelected?: boolean
): EvTableColumn[] => {
	return [
		{
			dataField: 'sessionName',
			text: 'Session',
			style: { width: '200px', maxWidth: '200px' },
			sort: true,
			formatter: (displayName: string, session: IWfaSessionRow) => {
				// todo change to sessions paths
				return (
					<EvLinkLocal
						className="wfa-session-name"
						id={session.sessionId}
						url={RoutesHelper.getWfaSessionPath(session.sessionId, SessionViews.OVERVIEW)}
						ariaLabel={`${displayName} Session`}
						linkText={session.sessionName || NAME_NOT_PROVIDED}
						isDark
					/>
				);
			}
		},
		{
			dataField: 'totalIssues',
			text: 'Total Issues',
			style: { width: '90px' },
			headerStyle: { width: '90px' },
			sort: true,
			formatter: renderNumberCellFormatter
		},
		{
			dataField: 'criticalIssues',
			text: 'Critical Issues',
			style: { width: '100px' },
			headerStyle: { width: '100px' },
			sort: true,
			formatter: renderNumberCellFormatter
		},
		{
			dataField: 'runTime',
			text: 'Last Modified',
			style: { width: '90px' },
			headerStyle: { width: '90px' },
			sort: true,
			formatter: (cell: string): string => formatShortMonthDatetime(cell)
		},
		{
			dataField: 'createdBy',
			text: 'Created by',
			style: { width: '135px' },
			headerStyle: { width: '135px' },
			sort: true
		},
		{
			dataField: 'labels',
			text: 'Labels',
			style: { width: '182px' },
			headerStyle: { width: '182px' },
			formatter: (labels: string[]) => {
				const displayLabels = transformLabelsForChips(
					labels,
					isNewPlatformEnabled ?? false,
					unitIdDisplayName
						? {
								displayName: unitIdDisplayName,
								labelConfig: unitIdLabelConfig,
								extractValue: (label: string) => extractLabelValue(label, UNIT_ID_LABEL_PREFIX),
								isLabelString: (label: string) => checkIsLabelString(label, UNIT_ID_LABEL_PREFIX)
							}
						: undefined,
					environmentDisplayName
						? {
								displayName: environmentDisplayName,
								labelConfig: environmentLabelConfig,
								extractValue: (label: string) => extractLabelValue(label, ENVIRONMENT_LABEL_PREFIX),
								isLabelString: (label: string) =>
									checkIsLabelString(label, ENVIRONMENT_LABEL_PREFIX)
							}
						: undefined
				);
				return <LabelsChips labels={displayLabels} dataAutomationId={LABELS_CHIPS_DAID} />;
			}
		},
		...(onDeleteSession
			? [
					{
						dataField: 'actions',
						text: 'Actions',
						style: ACTIONS_COLUMN_STYLE,
						headerStyle: ACTIONS_COLUMN_STYLE,
						classes: 'actions-cell-td',
						// formatExtraData is not in EvTableColumn type but is supported by react-bootstrap-table-next.
						// It is the correct way to pass dynamic values to formatters so the library
						// knows to re-render cells when the value changes (capturing state in a closure
						// alone is not enough — the library only re-renders on row data or formatExtraData change).
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						formatExtraData: { isDeleteDisabled: hasMultipleSelected },
						formatter: (
							_: unknown,
							session: IWfaSessionRow,
							_rowIndex: number,
							extraData: { isDeleteDisabled: boolean }
						) => (
							<RowActionsMenu
								id={`wfa-session-actions-${session.sessionId}`}
								triggerAriaLabel={`Actions for ${session.sessionName || NAME_NOT_PROVIDED}`}
								onDelete={() => onDeleteSession(session.sessionId)}
								entityName="session"
								isDeleteDisabled={extraData.isDeleteDisabled}
							/>
						)
					}
				]
			: [])
	].filter(returnNonNull);
};
